import type { BookingPreferences, SelectedSlot } from "../bookingFlowMachine"
import { bookingIneligibilityReason, isBookingConsentRequired } from "../errorReporting"
import { BookedAppointment, ScreeningProgram, useBookAppointment, useBookingOffering } from "../hooks"
import { appointmentTypeCopy } from "../preferencesCopy"
import { AppointmentAvailability } from "./AppointmentAvailability"
import { UsaStatesAndDc } from "@internal/constants"
import {
  Alert,
  BackIcon,
  Box,
  ForwardIcon,
  IconWithBackground,
  createLucideIcon,
  Tile,
  Typography
} from "@carrotfertility/carotene-core"
import {
  Form,
  FormAutocomplete,
  FormButton,
  type FormSelectOption,
  useFormContext,
  useWatch
} from "@carrotfertility/carotene-core-x"
import React from "react"
import { useIntl } from "react-intl"
import { BookingBreadcrumbs } from "../Breadcrumbs"
import { LoadingPage } from "./LoadingPage"
import { Heart } from "lucide-react"
import { useTrackBookingScreenView, useBookingTracking } from "../tracking"
import { getDisplayTimeZone } from "../timezone"

const HeartIcon = createLucideIcon(Heart, "HeartIcon")

type SelectPreferencesPageProps = {
  program: ScreeningProgram
  onConfirmBooking: (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) => void
  onDisqualified: (exclusionMessageId: string) => void
  onNeedsConsent: () => void
  onReturnToProviderResults?: () => void
  onBack: () => void
  timeZone?: string
}

type PreferencesFormValues = {
  offeringId: string
  // FormAutocomplete stores the selected option object (or null), not a bare value.
  region: FormSelectOption | null
  // Set by AppointmentAvailability once the member picks a date + time.
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

// The screen's own copy is English literal (see preferencesCopy.ts); the location-of-care
// options are localized here from the shared @internal/constants state list.
export function SelectPreferencesPage({
  program,
  onConfirmBooking,
  onDisqualified,
  onNeedsConsent,
  onReturnToProviderResults,
  onBack,
  timeZone
}: SelectPreferencesPageProps): JSX.Element {
  const intl = useIntl()
  const offeringQuery = useBookingOffering(program)
  const bookAppointment = useBookAppointment()
  const tracking = useBookingTracking()
  const resolvedOffering =
    offeringQuery.data?.offerings.find((offering) => offering.bookable) ?? offeringQuery.data?.offerings[0]
  useTrackBookingScreenView(
    !offeringQuery.isLoading && !offeringQuery.isError && resolvedOffering
      ? { screen: "scheduling", appointmentType: resolvedOffering.appointmentType }
      : null
  )

  if (offeringQuery.isLoading) {
    return <LoadingPage onReturnToProviderResults={onReturnToProviderResults} />
  }

  if (offeringQuery.isError || !offeringQuery.data) {
    return <Alert severity="error">Something went wrong loading your options. Please try again.</Alert>
  }

  const bookingOffering = offeringQuery.data
  const offerings = bookingOffering.offerings
  // The summary line and default selection follow the primary bookable offering (the initial
  // consult on a first visit).
  const primaryOffering = offerings.find((offering) => offering.bookable) ?? offerings[0]

  // 50 states + DC only: a clinician cannot hold a state medical license in a territory, so
  // offering one would be a dead end.
  const regionOptions = UsaStatesAndDc.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))

  const handleSubmit = async (values: PreferencesFormValues) => {
    // The Confirm button is disabled until a slot is chosen; guard anyway.
    if (!values.selectedSlot) return
    const slot = values.selectedSlot
    const attemptNumber = tracking.onBookingSubmitted(primaryOffering.appointmentType)
    try {
      const displayTimeZone = getDisplayTimeZone(timeZone)
      // Place the booking, then advance — the confirmation screen needs the returned
      // appointment (assigned provider + visit window). A failure surfaces inline (see
      // AppointmentAvailability) and leaves the member here to retry.
      const appointment = await bookAppointment.mutateAsync({
        program,
        slotToken: slot.slotToken,
        // The member doesn't pick a provider; take the first eligible host for the slot.
        hostId: slot.availableHostIds[0],
        timeZone: displayTimeZone
      })
      tracking.onEvent({
        type: "success",
        appointmentId: appointment.appointmentId,
        hostId: appointment.hostId,
        bookingAttemptNumber: attemptNumber
      })
      onConfirmBooking(
        {
          offeringId: values.offeringId,
          region: values.region ? String(values.region.value) : ""
        },
        slot,
        appointment
      )
    } catch (error) {
      // Eligibility rejection → out; unsigned-consent gate → back to consent; anything else → inline retry.
      const ineligibleReason = bookingIneligibilityReason(error)
      if (ineligibleReason !== null) {
        tracking.onEvent({ type: "gating_result", result: "ineligible", resultSource: "booking_recheck" })
        onDisqualified(ineligibleReason)
      } else if (isBookingConsentRequired(error)) {
        onNeedsConsent()
      }
    }
  }

  return (
    <Form<PreferencesFormValues>
      onSubmit={handleSubmit}
      defaultValues={{ offeringId: primaryOffering?.id ?? "", region: null, selectedDate: null, selectedSlot: null }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <BookingBreadcrumbs onReturnToSearchResults={onReturnToProviderResults} />
          <Alert severity="error" sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.lg) })}>
            For emergencies, please call 911 or seek immediate medical attention.
          </Alert>
        </Box>

        <Typography variant="h2" component="h1">
          Lets find a time that works
        </Typography>

        <Tile
          variant="primary"
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Box sx={(theme) => ({ display: "flex", alignItems: "center", gap: theme.spacing(theme.tokens.spacing.sm) })}>
            <IconWithBackground color="primary" icon={HeartIcon} />
            <Typography variant="h3">
              {primaryOffering.durationMinutes +
                " minute " +
                appointmentTypeCopy(primaryOffering.appointmentType).title}
            </Typography>
          </Box>
          <Typography variant="body1">{appointmentTypeCopy(primaryOffering.appointmentType).description}</Typography>
        </Tile>

        <Box sx={{ maxInlineSize: 440 }}>
          <FormAutocomplete
            name="region"
            label="Please confirm your location"
            controllerProps={{ rules: { required: "Please select your location of care." } }}
            fullWidth
            options={regionOptions}
            helperText="Select the state where you'll be during your visit"
          />
        </Box>

        <AvailabilityStep
          program={program}
          onBack={onBack}
          booking={{ isPending: bookAppointment.isLoading, isError: bookAppointment.isError }}
          timeZone={timeZone}
        />
      </Box>
    </Form>
  )
}

// Two-phase, on one screen: the member first submits their visit type + location ("See
// availability"), which reveals the date + time picker (AppointmentAvailability owns that and
// the "Confirm booking"/"Continue" CTA). Lives inside <Form> so it can validate + read the
// preference fields. Editing the visit type or location after revealing hides availability
// again — the shown slots were for the old preferences.
function AvailabilityStep({
  program,
  onBack,
  booking,
  timeZone
}: {
  program: ScreeningProgram
  onBack: () => void
  booking: { isPending: boolean; isError: boolean }
  timeZone?: string
}): JSX.Element {
  const { trigger, getValues, setValue } = useFormContext<PreferencesFormValues>()
  const offeringId = useWatch<PreferencesFormValues>({ name: "offeringId" })
  const region = useWatch<PreferencesFormValues>({ name: "region" })
  const stateCode = region ? String(region.value) : ""
  const tracking = useBookingTracking()

  // The preferences the member submitted to reveal availability; null until they do.
  const [submitted, setSubmitted] = React.useState<{ offeringId: string; stateCode: string } | null>(null)
  React.useEffect(() => {
    if (!submitted) tracking.onEvent({ type: "availability", searchKey: "", outcome: null })
  }, [submitted, tracking])

  React.useEffect(() => {
    if (submitted && (submitted.offeringId !== offeringId || submitted.stateCode !== stateCode)) {
      setSubmitted(null)
      setValue("selectedDate", null)
      setValue("selectedSlot", null)
    }
  }, [offeringId, stateCode, submitted, setValue])

  const onSeeAvailability = async () => {
    // Submitting the preferences: validate the required fields, then reveal availability.
    const valid = await trigger(["offeringId", "region"])
    if (!valid) return
    const values = getValues()
    setSubmitted({
      offeringId: values.offeringId,
      stateCode: values.region ? String(values.region.value) : ""
    })
  }

  const backToPreferences = () => {
    setSubmitted(null)
    setValue("region", null)
    setValue("selectedDate", null)
    setValue("selectedSlot", null)
  }

  if (!submitted) {
    return (
      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(theme.tokens.spacing.sm)
        })}
      >
        <FormButton color="secondary" variant="text" startIcon={<BackIcon />} onClick={onBack}>
          Back
        </FormButton>
        <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />} onClick={onSeeAvailability}>
          Continue
        </FormButton>
      </Box>
    )
  }

  return (
    <AppointmentAvailability
      program={program}
      offeringId={submitted.offeringId}
      stateCode={submitted.stateCode}
      booking={booking}
      onBack={backToPreferences}
      timeZone={timeZone}
    />
  )
}
